import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { SiteHeader } from "@/components/site/SiteHeader";
import { API, useAuth } from "@/App";
import LoginRequiredDialog from "@/components/LoginRequiredDialog";
import { useLoginGuard } from "@/hooks/useLoginGuard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Banknote,
  CreditCard,
  Shield,
  ExternalLink,
  Check,
  ArrowRight,
  User,
  LogOut,
  Percent,
  FileText,
  Building,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  IndianRupee,
  Calendar,
  CheckCircle2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_097dcc50-4812-4b75-a884-fb87f03fa7a9/artifacts/6cfgy4bw_logo%20%281%29.webp";

// Random gradient backgrounds for cards
const cardGradients = [
  "from-blue-500 to-indigo-600",
  "from-purple-500 to-pink-500",
  "from-fuchsia-500 to-pink-500",
  "from-amber-500 to-red-500",
  "from-cyan-500 to-blue-500",
  "from-rose-500 to-pink-500",
  "from-violet-500 to-purple-600",
  "from-amber-500 to-amber-500",
];

// Finance images
const financeImages = {
  personal_loan: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop",
  credit_card: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop",
  insurance: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&h=300&fit=crop",
};

const FinancePage = () => {
  const navigate = useNavigate();
  const { user, logout, token, getAuthHeaders } = useAuth();
  const { requireLogin, dialogOpen, setDialogOpen, dialogMessage } = useLoginGuard();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  
  const [applicationForm, setApplicationForm] = useState({
    full_name: "",
    email: "",
    mobile: "",
    pan_number: "",
    employment_type: "salaried",
    company_name: "",
    monthly_income: "",
    loan_amount: "",
    tenure_months: "12",
    city: "",
    pin_code: "",
    purpose: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/finance/products`),
        axios.get(`${API}/finance/categories`),
      ]);
      setProducts(productsRes.data.products || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error("Failed to fetch finance data:", error);
    }
    setLoading(false);
  };

  const handleProductClick = async (product) => {
    try {
      const headers = token ? getAuthHeaders() : {};
      const res = await axios.post(
        `${API}/track/click?item_type=financial_product&item_id=${product.id}`,
        {},
        { headers }
      );
      window.open(res.data.redirect_url, "_blank");
    } catch (error) {
      window.open(product.affiliate_link, "_blank");
    }
  };

  const handleApplyNow = (product) => {
    if (!user) {
      requireLogin(
        null,
        `Please login to apply for ${product.product_name || product.name || "this product"}.`
      );
      return;
    }
    setSelectedProduct(product);
    setApplicationSuccess(false);
    // Pre-fill user data
    setApplicationForm(prev => ({
      ...prev,
      full_name: user.name || "",
      email: user.email || "",
      mobile: user.mobile || "",
    }));
    setApplyDialogOpen(true);
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...applicationForm,
        product_id: selectedProduct.id,
        product_name: selectedProduct.name,
        company_name: selectedProduct.company_name,
        category: selectedProduct.category,
        monthly_income: parseFloat(applicationForm.monthly_income),
        loan_amount: applicationForm.loan_amount ? parseFloat(applicationForm.loan_amount) : null,
        tenure_months: parseInt(applicationForm.tenure_months),
      };

      const headers = token ? getAuthHeaders() : {};
      await axios.post(`${API}/finance/applications`, payload, { headers });
      
      setApplicationSuccess(true);
      
      // Track click and redirect after showing success
      setTimeout(() => {
        handleProductClick(selectedProduct);
        setApplyDialogOpen(false);
        setApplicationForm({
          full_name: "",
          email: "",
          mobile: "",
          pan_number: "",
          employment_type: "salaried",
          company_name: "",
          monthly_income: "",
          loan_amount: "",
          tenure_months: "12",
          city: "",
          pin_code: "",
          purpose: "",
        });
      }, 2000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    }
    setSubmitting(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const filteredProducts = selectedCategory === "all"
    ? products
    : products.filter(p => p.category === selectedCategory);

  const getCategoryIcon = (category) => {
    switch (category) {
      case "credit_card": return CreditCard;
      case "insurance": return Shield;
      default: return Banknote;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <LoginRequiredDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        message={dialogMessage}
      />
      {/* Header */}
      <SiteHeader />

      {/* Hero Section with Gradient */}
      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <Badge className="bg-white/20 text-white mb-4">
            Financial Services Partner
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Smart Financial Solutions
            <br />
            <span className="text-yellow-400">Compare. Apply. Save.</span>
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
            Get instant approvals on personal loans, credit cards, and insurance. 
            Earn commission on every successful application through our partner network.
          </p>

          {/* Category Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-12">
            {[
              { id: "personal_loan", name: "Personal Loans", icon: Banknote, desc: "Up to ₹50 Lakhs", gradient: "from-blue-400 to-blue-600" },
              { id: "credit_card", name: "Credit Cards", icon: CreditCard, desc: "Lifetime Free", gradient: "from-purple-400 to-purple-600" },
              { id: "insurance", name: "Insurance", icon: Shield, desc: "₹1 Cr+ Cover", gradient: "from-fuchsia-400 to-fuchsia-600" },
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id === selectedCategory ? "all" : cat.id)}
                className={`group relative p-6 rounded-2xl border-2 transition-all overflow-hidden ${
                  selectedCategory === cat.id
                    ? "border-white bg-white/20"
                    : "border-white/30 bg-white/10 hover:border-white/50 hover:bg-white/15"
                }`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${cat.gradient} opacity-0 group-hover:opacity-20 transition-opacity`}></div>
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <cat.icon className="w-7 h-7 text-indigo-600" />
                  </div>
                  <h3 className="font-semibold text-lg">{cat.name}</h3>
                  <p className="text-white/70 text-sm mt-1">{cat.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-3xl font-bold text-gray-900">
                {selectedCategory === "all"
                  ? "All Financial Products"
                  : selectedCategory === "personal_loan"
                    ? "Personal Loans"
                    : selectedCategory === "credit_card"
                      ? "Credit Cards"
                      : "Insurance Plans"}
              </h2>
              <p className="text-gray-500 mt-1">Compare and apply for the best offers</p>
            </div>
            {selectedCategory !== "all" && (
              <Button variant="ghost" onClick={() => setSelectedCategory("all")}>
                View All
              </Button>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-80 bg-gray-100 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts.map((product, idx) => {
                const Icon = getCategoryIcon(product.category);
                const gradient = cardGradients[idx % cardGradients.length];
                
                return (
                  <Card
                    key={product.id}
                    className="border-0 shadow-lg hover:shadow-xl transition-all overflow-hidden group"
                    data-testid={`finance-product-${product.id}`}
                  >
                    {/* Card Header with Gradient */}
                    <div className={`h-32 bg-gradient-to-br ${gradient} relative`}>
                      <div className="absolute inset-0 bg-black/10"></div>
                      <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
                        {product.logo_url ? (
                          <img
                            src={product.logo_url}
                            alt={product.company_name}
                            className="w-14 h-14 object-contain bg-white rounded-xl p-2"
                          />
                        ) : (
                          <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center">
                            <Icon className="w-7 h-7 text-indigo-600" />
                          </div>
                        )}
                        <Badge className="bg-white/90 text-gray-800">
                          <Percent className="w-3 h-3 mr-1" />
                          {product.commission_percent}% Earn
                        </Badge>
                      </div>
                      <div className="absolute bottom-4 left-4 right-4 text-white">
                        <p className="text-sm opacity-80">{product.company_name}</p>
                        <h3 className="font-semibold text-lg">{product.name}</h3>
                      </div>
                    </div>

                    <CardContent className="p-5">
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {product.description}
                      </p>

                      {/* Features */}
                      {product.features && product.features.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {product.features.slice(0, 3).map((feature, fidx) => (
                            <div key={fidx} className="flex items-center gap-2 text-sm">
                              <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                              <span className="text-gray-600">{feature}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Details */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {product.interest_rate && (
                          <Badge className="bg-blue-50 text-blue-700">
                            {product.interest_rate}
                          </Badge>
                        )}
                        {product.min_amount && product.max_amount && (
                          <Badge className="bg-gray-100 text-gray-700">
                            ₹{(product.min_amount / 1000).toFixed(0)}K - ₹{(product.max_amount / 100000).toFixed(0)}L
                          </Badge>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApplyNow(product)}
                          className={`flex-1 bg-gradient-to-r ${gradient} hover:opacity-90`}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Apply Now
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleProductClick(product)}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-2xl">
              <Banknote className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No financial products available in this category</p>
            </div>
          )}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-gray-500">Simple steps to get your financial product</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: 1, title: "Choose Product", desc: "Select from loans, cards, or insurance", icon: FileText },
              { step: 2, title: "Fill Details", desc: "Complete the simple application form", icon: User },
              { step: 3, title: "Get Approved", desc: "Instant approval from partner", icon: CheckCircle2 },
              { step: 4, title: "Earn Commission", desc: "Get rewarded for successful referral", icon: IndianRupee },
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-sm text-indigo-600 font-semibold mb-2">Step {item.step}</div>
                <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Dialog */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          {applicationSuccess ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h3>
              <p className="text-gray-500 mb-4">
                Your application has been submitted successfully. You'll be redirected to the partner website.
              </p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {selectedProduct?.logo_url ? (
                    <img src={selectedProduct.logo_url} alt="" className="w-10 h-10 object-contain" />
                  ) : (
                    <Banknote className="w-6 h-6 text-indigo-600" />
                  )}
                  Apply for {selectedProduct?.name}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmitApplication} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        value={applicationForm.full_name}
                        onChange={(e) => setApplicationForm({...applicationForm, full_name: e.target.value})}
                        placeholder="Enter your full name"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Mobile Number *</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        value={applicationForm.mobile}
                        onChange={(e) => setApplicationForm({...applicationForm, mobile: e.target.value})}
                        placeholder="10-digit mobile"
                        className="pl-10"
                        maxLength={10}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email Address *</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="email"
                        value={applicationForm.email}
                        onChange={(e) => setApplicationForm({...applicationForm, email: e.target.value})}
                        placeholder="your@email.com"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>PAN Number *</Label>
                    <div className="relative mt-1">
                      <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        value={applicationForm.pan_number}
                        onChange={(e) => setApplicationForm({...applicationForm, pan_number: e.target.value.toUpperCase()})}
                        placeholder="ABCDE1234F"
                        className="pl-10 uppercase"
                        maxLength={10}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Employment Type *</Label>
                    <Select 
                      value={applicationForm.employment_type} 
                      onValueChange={(v) => setApplicationForm({...applicationForm, employment_type: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="salaried">Salaried</SelectItem>
                        <SelectItem value="self_employed">Self Employed</SelectItem>
                        <SelectItem value="business">Business Owner</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Company/Business Name *</Label>
                    <div className="relative mt-1">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        value={applicationForm.company_name}
                        onChange={(e) => setApplicationForm({...applicationForm, company_name: e.target.value})}
                        placeholder="Company name"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Monthly Income (₹) *</Label>
                    <div className="relative mt-1">
                      <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="number"
                        value={applicationForm.monthly_income}
                        onChange={(e) => setApplicationForm({...applicationForm, monthly_income: e.target.value})}
                        placeholder="50000"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  {selectedProduct?.category === "personal_loan" && (
                    <div>
                      <Label>Loan Amount Required (₹)</Label>
                      <div className="relative mt-1">
                        <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="number"
                          value={applicationForm.loan_amount}
                          onChange={(e) => setApplicationForm({...applicationForm, loan_amount: e.target.value})}
                          placeholder="500000"
                          className="pl-10"
                        />
                      </div>
                    </div>
                  )}
                  {selectedProduct?.category === "personal_loan" && (
                    <div>
                      <Label>Tenure (Months)</Label>
                      <Select 
                        value={applicationForm.tenure_months} 
                        onValueChange={(v) => setApplicationForm({...applicationForm, tenure_months: v})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="12">12 Months</SelectItem>
                          <SelectItem value="24">24 Months</SelectItem>
                          <SelectItem value="36">36 Months</SelectItem>
                          <SelectItem value="48">48 Months</SelectItem>
                          <SelectItem value="60">60 Months</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>City *</Label>
                    <div className="relative mt-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        value={applicationForm.city}
                        onChange={(e) => setApplicationForm({...applicationForm, city: e.target.value})}
                        placeholder="Your city"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>PIN Code *</Label>
                    <div className="relative mt-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        value={applicationForm.pin_code}
                        onChange={(e) => setApplicationForm({...applicationForm, pin_code: e.target.value})}
                        placeholder="6-digit PIN"
                        className="pl-10"
                        maxLength={6}
                        required
                      />
                    </div>
                  </div>
                </div>

                {selectedProduct?.category === "personal_loan" && (
                  <div>
                    <Label>Purpose of Loan</Label>
                    <Textarea
                      value={applicationForm.purpose}
                      onChange={(e) => setApplicationForm({...applicationForm, purpose: e.target.value})}
                      placeholder="Brief description of why you need this loan..."
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                )}

                <div className="bg-blue-50 p-4 rounded-xl">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> By submitting this application, you agree to share your information with {selectedProduct?.company_name} for processing your application.
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "Submit Application"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancePage;
